import { useCallback, useRef, useState } from "react";

import { describeError } from "../api/errors";
import { grantTokens } from "../api/endpoints";
import { newRequestId } from "../utils/requestId";
import type { GrantResult } from "../api/types";

/**
 * How a finished attempt ended.
 *
 * `not_notified` is a success and not an error, and keeping it out of the error state is the
 * point of having it: the tokens are on the balance, only the message did not go out, and an
 * admin who reads a failure repeats the action - which would credit a second time.
 */
export type GrantOutcome = "done" | "not_notified" | "replayed" | null;

export interface UseGrant {
  granting: boolean;
  outcome: GrantOutcome;
  error: string | null;
  result: GrantResult | null;
  grant: (userId: number, tokens: number, comment?: string) => void;
  reset: () => void;
}

function getOutcome(result: GrantResult): GrantOutcome {
  if (!result.credited) {
    return "replayed";
  }
  return result.notified ? "done" : "not_notified";
}

export function useGrant(): UseGrant {
  const [granting, setGranting] = useState(false);
  const [outcome, setOutcome] = useState<GrantOutcome>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GrantResult | null>(null);

  /**
   * Identifier of the attempt in flight, kept across retries of that same attempt.
   *
   * This is what makes a retry safe rather than a second gift: the api derives the ledger key
   * from it, so repeating a call that timed out lands on the row the first one wrote. It is
   * cleared after a finished attempt, so the next grant - a deliberate second one to the same
   * client, say - is a new attempt and credits again.
   */
  const attemptId = useRef<string | null>(null);

  const reset = useCallback(() => {
    attemptId.current = null;
    setOutcome(null);
    setError(null);
    setResult(null);
  }, []);

  const grant = useCallback(
    async (userId: number, tokens: number, comment?: string) => {
      if (granting) {
        return;
      }

      attemptId.current ??= newRequestId();

      setGranting(true);
      setError(null);
      setOutcome(null);
      try {
        const answer = await grantTokens(userId, tokens, attemptId.current, comment);
        // done with this attempt: the next grant must not reuse its key, or it would credit
        // nothing and quietly report success
        attemptId.current = null;
        setResult(answer);
        setOutcome(getOutcome(answer));
      } catch (failure) {
        // the attempt id is deliberately kept: this may be a timeout on a call that did
        // credit, and pressing the button again has to be the same attempt, not a second one
        setError(describeError(failure));
        setResult(null);
      } finally {
        setGranting(false);
      }
    },
    [granting],
  );

  return { granting, outcome, error, result, grant, reset };
}
